'use client';

import { c as _c } from "react/compiler-runtime";
import type { CSSProperties, PropsWithChildren } from 'react';
import { cn } from '@morpho-org/ui-v6/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Dialog as DialogRadix } from 'radix-ui';
import { useEffect } from 'react';
type OnInteractOutside = React.ComponentProps<typeof DialogRadix.Content>['onInteractOutside'];
type CssLength = string | number | undefined;
const toCssLength = (v: CssLength, fallback: string) => v === undefined ? fallback : typeof v === 'number' ? `${v}px` : v;

/**
 * Dialog content variants using cva
 */
const dialogContentVariants = cva(['fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2', 'bg-popover rounded-lg', 'overflow-y-auto', 'z-50', 'flex flex-col', 'focus:outline-none', 'animate-dialog-in'], {
  variants: {
    fullSize: {
      true: 'w-screen',
      false: 'w-[90vw]'
    }
  },
  defaultVariants: {
    fullSize: false
  }
});

/**
 * Overlay variants using cva
 */
const overlayVariants = cva(['fixed inset-0', 'bg-black/30 backdrop-blur-[8px]', 'z-50', 'h-screen', 'touch-none', 'animate-fade-in']);
export interface DialogOptions extends VariantProps<typeof dialogContentVariants> {
  /**
   * @deprecated Use `className` with Tailwind classes instead (e.g., `max-w-[580px]`)
   */
  maxWidth?: CSSProperties['maxWidth'];
  /**
   * @deprecated Use `className` with Tailwind classes instead (e.g., `max-h-[95vh]`)
   */
  maxHeight?: CSSProperties['maxHeight'];
  /**
   * @deprecated Use `className` with Tailwind classes instead (e.g., `h-auto`)
   */
  height?: CSSProperties['height'];
  closable?: boolean;
  /**
   * Tailwind padding classes for the dialog content (default `p-lg`).
   */
  padding?: string;
  /**
   * Additional Tailwind classes for styling the dialog content
   */
  className?: string;
  onInteractOutside?: OnInteractOutside;
  onOpenAutoFocus?: DialogRadix.DialogContentProps['onOpenAutoFocus'];
  /**
   * Accessible name for the dialog, announced by screen readers when it opens. Rendered into
   * the otherwise-empty Radix `Dialog.Title`, visually hidden — the visible heading stays
   * whatever the content renders.
   */
  ariaLabel?: string;
  /**
   * Callback fired when the dialog is dismissed externally (overlay click, Escape, browser navigation).
   * Use this to sync parent state that opened the dialog.
   */
  onClose?: () => void;
}
export interface DialogProps extends PropsWithChildren {
  dialogOptions?: DialogOptions;
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * Dialog component - Tailwind version
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Dialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <p>Dialog content</p>
 * </Dialog>
 *
 * // With responsive padding
 * <Dialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   dialogOptions={{
 *     padding: { base: "m", m: "s" },
 *     maxWidth: "600px",
 *   }}
 * >
 *   <p>Dialog content</p>
 * </Dialog>
 *
 * // Full size dialog
 * <Dialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   dialogOptions={{ fullSize: true }}
 * >
 *   <p>Full size content</p>
 * </Dialog>
 * ```
 */
export function Dialog(t0) {
  const $ = _c(40);
  const {
    children,
    dialogOptions,
    isOpen,
    onClose
  } = t0;
  let t1;
  let t2;
  if ($[0] !== isOpen) {
    t1 = () => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    };
    t2 = [isOpen];
    $[0] = isOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let T0;
  let T1;
  let T2;
  let ariaLabel;
  let onInteractOutside;
  let onOpenAutoFocus;
  let style;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] !== dialogOptions || $[4] !== isOpen || $[5] !== onClose) {
    const {
      fullSize,
      maxWidth,
      maxHeight,
      height,
      padding: t7,
      className,
      onInteractOutside: t8,
      onOpenAutoFocus: t9,
      ariaLabel: t10
    } = dialogOptions ?? {};
    onInteractOutside = t8;
    onOpenAutoFocus = t9;
    ariaLabel = t10;
    const padding = t7 === undefined ? "p-lg" : t7;
    style = {
      "--dialog-max-width": toCssLength(fullSize ? "min(calc(100vw - 60px), 1280px)" : maxWidth, "580px"),
      "--dialog-max-height": toCssLength(maxHeight, "95vh"),
      "--dialog-height": toCssLength(height, "auto")
    };
    T2 = DialogRadix.Root;
    t5 = isOpen;
    t6 = false;
    T1 = DialogRadix.Portal;
    if ($[17] !== isOpen || $[18] !== onClose) {
      t4 = isOpen && <div data-slot="dialog-overlay" className={overlayVariants()} onClick={onClose} />;
      $[17] = isOpen;
      $[18] = onClose;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    T0 = DialogRadix.Content;
    t3 = cn(dialogContentVariants({
      fullSize
    }), padding, "max-md:p-md", "md:max-w-(--dialog-max-width) md:max-h-(--dialog-max-height) md:h-(--dialog-height)", "max-md:inset-x-0 max-md:top-auto max-md:bottom-0 max-md:translate-x-0 max-md:translate-y-0", "max-md:h-auto max-md:max-h-[90dvh] max-md:w-full max-md:max-w-none", "max-md:rounded-t-[20px] max-md:rounded-b-none", "max-md:animate-in max-md:fade-in max-md:slide-in-from-bottom-full max-md:duration-300", "max-md:motion-reduce:[--tw-enter-translate-y:0]", className);
    $[3] = dialogOptions;
    $[4] = isOpen;
    $[5] = onClose;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = ariaLabel;
    $[10] = onInteractOutside;
    $[11] = onOpenAutoFocus;
    $[12] = style;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    ariaLabel = $[9];
    onInteractOutside = $[10];
    onOpenAutoFocus = $[11];
    style = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
  }
  let t7;
  if ($[20] !== ariaLabel) {
    t7 = <DialogRadix.Title className="sr-only">{ariaLabel}</DialogRadix.Title>;
    $[20] = ariaLabel;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogRadix.Description />;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== T0 || $[24] !== children || $[25] !== onInteractOutside || $[26] !== onOpenAutoFocus || $[27] !== style || $[28] !== t3 || $[29] !== t7) {
    t9 = <T0 className={t3} style={style} onInteractOutside={onInteractOutside} onOpenAutoFocus={onOpenAutoFocus}>{t7}{t8}{children}</T0>;
    $[23] = T0;
    $[24] = children;
    $[25] = onInteractOutside;
    $[26] = onOpenAutoFocus;
    $[27] = style;
    $[28] = t3;
    $[29] = t7;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== T1 || $[32] !== t4 || $[33] !== t9) {
    t10 = <T1>{t4}{t9}</T1>;
    $[31] = T1;
    $[32] = t4;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== T2 || $[36] !== t10 || $[37] !== t5 || $[38] !== t6) {
    t11 = <T2 open={t5} modal={t6}>{t10}</T2>;
    $[35] = T2;
    $[36] = t10;
    $[37] = t5;
    $[38] = t6;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
}